import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { getInitials, roleLabels, roleColors } from '../lib/utils';
import {
  User,
  Users,
  Bell,
  Shield,
  Palette,
  Moon,
  Sun,
  Building2,
  Clock,
  ChevronRight,
  Zap,
  MessageSquare,
  SlidersHorizontal,
  Plus,
  Trash2,
  BookOpen,
  RefreshCw,
  Globe,
  Play,
  Loader2,
  RotateCcw,
  Wrench,
  Sparkles,
  FileCheck,
} from 'lucide-react';

const LOGO_URL = '/estrapat-logo.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, setUser, isAdmin, isMaster } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [cedulaRcForm, setCedulaRcForm] = useState({
    cedula_inicio: user?.cedula_inicio || '',
    cedula_vencimiento: user?.cedula_vencimiento || '',
    rc_inicio: user?.rc_inicio || '',
    rc_vencimiento: user?.rc_vencimiento || '',
  });
  const [savingCedulaRc, setSavingCedulaRc] = useState(false);
  const [promoWhatsapp, setPromoWhatsapp] = useState('');
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [aiModelChat, setAiModelChat] = useState('');
  const [aiModelExtraction, setAiModelExtraction] = useState('');
  const [aiModelRag, setAiModelRag] = useState('');
  const [aiModelChoices, setAiModelChoices] = useState([]);
  const [savingAiModels, setSavingAiModels] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [schedulers, setSchedulers] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [newField, setNewField] = useState({ name: '', label: '', field_type: 'texto', options: '' });
  const [savingField, setSavingField] = useState(false);
  const [policyCustomFields, setPolicyCustomFields]   = useState([]);
  const [newPolicyField, setNewPolicyField]           = useState({ label: '', field_type: 'texto', options: '' });
  const [savingPolicyField, setSavingPolicyField]     = useState(false);
  const [syncingDrive, setSyncingDrive] = useState(false);
  const [syncDriveResult, setSyncDriveResult] = useState(null);
  const [syncingSites, setSyncingSites] = useState(false);
  const [syncSitesResult, setSyncSitesResult] = useState(null);
  const [backfillingProductos, setBackfillingProductos] = useState(false);
  const [backfillProductosResult, setBackfillProductosResult] = useState(null);
  const [forceSyncingSites, setForceSyncingSites] = useState(false);
  const [runningScheduler, setRunningScheduler] = useState(false);
  const [notificationTime, setNotificationTime] = useState('08:00');
  const [summaryTime, setSummaryTime] = useState('09:00');
  const [savingNotificationTime, setSavingNotificationTime] = useState(false);
  const [savingSummaryTime, setSavingSummaryTime] = useState(false);
  const [inactivityDays, setInactivityDays] = useState(7);
  const [savingInactivityDays, setSavingInactivityDays] = useState(false);
  const [runningInactivityCheck, setRunningInactivityCheck] = useState(false);
  const [inactivityCheckModalOpen, setInactivityCheckModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncingPolicies, setSyncingPolicies] = useState(false);
  const [meetingStaff, setMeetingStaff] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [savingStaff, setSavingStaff] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setPromoWhatsapp(res.data.promo_whatsapp_number || '');
        setNotificationsEnabled(res.data.notifications_enabled !== false);
        const h = String(res.data.notification_hour ?? 8).padStart(2, '0');
        const m = String(res.data.notification_minute ?? 0).padStart(2, '0');
        setNotificationTime(`${h}:${m}`);
        const sh = String(res.data.summary_hour ?? 9).padStart(2, '0');
        const sm = String(res.data.summary_minute ?? 0).padStart(2, '0');
        setSummaryTime(`${sh}:${sm}`);
        setMeetingStaff(res.data.meeting_staff || []);
        setAiModelChat(res.data.ai_model_chat || '');
        setAiModelExtraction(res.data.ai_model_extraction || '');
        setAiModelRag(res.data.ai_model_rag || '');
        setInactivityDays(res.data.inactivity_days ?? 7);
      })
      .catch(() => {});
    axios.get(`${API}/settings/ai-models`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAiModelChoices(res.data.models || []))
      .catch(() => {});
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const u = Array.isArray(res.data) ? res.data : (res.data.items || []);
        setStaffUsers(u.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es', { sensitivity: 'base' })));
      })
      .catch(() => {});
    axios.get(`${API}/settings/schedulers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setSchedulers(res.data.schedulers || {}))
      .catch(() => {});
    axios.get(`${API}/custom-fields`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCustomFields(res.data))
      .catch(() => {});
    axios.get(`${API}/policy-custom-fields`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPolicyCustomFields(res.data))
      .catch(() => {});
  }, [isAdmin]);

  const fetchCustomFields = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/custom-fields`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCustomFields(res.data))
      .catch(() => {});
  };

  const handleCreateField = async () => {
    if (!newField.label) {
      toast.error('La etiqueta del campo es requerida');
      return;
    }
    setSavingField(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        label: newField.label,
        field_type: newField.field_type,
        options: newField.field_type === 'lista'
          ? newField.options.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
      };
      await axios.post(`${API}/custom-fields`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campo creado correctamente');
      setNewField({ name: '', label: '', field_type: 'texto', options: '' });
      fetchCustomFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear campo');
    } finally {
      setSavingField(false);
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('¿Eliminar este campo? Se borrarán todos sus valores en todos los leads.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/custom-fields/${fieldId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campo eliminado');
      fetchCustomFields();
    } catch {
      toast.error('Error al eliminar campo');
    }
  };

  const fetchPolicyCustomFields = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/policy-custom-fields`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPolicyCustomFields(res.data))
      .catch(() => {});
  };

  const handleCreatePolicyField = async () => {
    if (!newPolicyField.label.trim()) { toast.error('La etiqueta es requerida'); return; }
    setSavingPolicyField(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/policy-custom-fields`, {
        label: newPolicyField.label,
        field_type: newPolicyField.field_type,
        options: newPolicyField.field_type === 'lista'
          ? newPolicyField.options.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campo de póliza creado');
      setNewPolicyField({ label: '', field_type: 'texto', options: '' });
      fetchPolicyCustomFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear campo');
    } finally {
      setSavingPolicyField(false);
    }
  };

  const handleDeletePolicyField = async (fieldId) => {
    if (!window.confirm('¿Eliminar este campo? Se borrarán todos sus valores en todas las pólizas.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/policy-custom-fields/${fieldId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campo eliminado');
      fetchPolicyCustomFields();
    } catch {
      toast.error('Error al eliminar campo');
    }
  };

  const handleToggleNotifications = async (val) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`,
        { notifications_enabled: val },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationsEnabled(val);
      toast.success(val ? 'Notificaciones activadas' : 'Notificaciones pausadas');
    } catch {
      toast.error('Error al cambiar configuración');
    }
  };

  const handleToggleScheduler = async (key, val) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.put(`${API}/settings/schedulers`,
        { key, enabled: val },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSchedulers(res.data.schedulers || {});
      const label = schedulers[key]?.label || key;
      toast.success(`Scheduler "${label}" ${val ? 'activado' : 'pausado'}`);
    } catch {
      toast.error('Error al cambiar scheduler');
    }
  };

  const handleRunSchedulerNow = async () => {
    setRunningScheduler(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/notification-jobs/run`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Scheduler ejecutado correctamente');
    } catch {
      toast.error('Error al ejecutar el scheduler');
    } finally {
      setRunningScheduler(false);
    }
  };

  const handleSyncPolicies = async (sendNotifications) => {
    setSyncingPolicies(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/policies/sync-pending-payment`,
        { send_notifications: sendNotifications },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      if (sendNotifications) {
        toast.success(`Sincronización completada — ${d.to_pending} a pendiente, ${d.to_active} a activo. ${d.notifications_sent ?? 0} notificaciones enviadas.`);
      } else {
        toast.success(`Sincronización completada — ${d.to_pending} a pendiente, ${d.to_active} a activo.`);
      }
      setSyncModalOpen(false);
    } catch {
      toast.error('Error al sincronizar pólizas');
    } finally {
      setSyncingPolicies(false);
    }
  };

  const [repairingEndDates, setRepairingEndDates] = useState(false);
  const handleRepairEndDates = async () => {
    setRepairingEndDates(true);
    try {
      const res = await axios.post(`${API}/admin/repair-end-dates`);
      const { repaired, skipped_ahorro, total_checked } = res.data;
      toast.success(`${repaired} póliza(s) reparada(s) de ${total_checked} revisada(s)${skipped_ahorro > 0 ? ` · ${skipped_ahorro} de Ahorro omitida(s)` : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reparar fechas de vencimiento');
    } finally {
      setRepairingEndDates(false);
    }
  };

  const [backfillingRenewals, setBackfillingRenewals] = useState(false);
  const [backfillModalOpen, setBackfillModalOpen] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const handleBackfillRenewals = async () => {
    setBackfillingRenewals(true);
    try {
      const res = await axios.post(`${API}/admin/backfill-renewal-movement-type`);
      setBackfillResult(res.data);
      setBackfillModalOpen(true);
      const { actualizadas, ya_marcadas_previamente } = res.data;
      toast.success(`${actualizadas} póliza(s) corregida(s)${ya_marcadas_previamente > 0 ? ` · ${ya_marcadas_previamente} ya estaban marcadas` : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al corregir renovaciones sin marcar');
    } finally {
      setBackfillingRenewals(false);
    }
  };

  const handleSaveNotificationTime = async () => {
    setSavingNotificationTime(true);
    try {
      const token = localStorage.getItem('token');
      const [h, m] = notificationTime.split(':').map(Number);
      await axios.put(`${API}/settings`,
        { notification_hour: h, notification_minute: m },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hora de envío guardada');
    } catch {
      toast.error('Error al guardar la hora');
    } finally {
      setSavingNotificationTime(false);
    }
  };

  const handleSaveSummaryTime = async () => {
    setSavingSummaryTime(true);
    try {
      const token = localStorage.getItem('token');
      const [h, m] = summaryTime.split(':').map(Number);
      await axios.put(`${API}/settings`,
        { summary_hour: h, summary_minute: m },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hora de resumen guardada');
    } catch {
      toast.error('Error al guardar la hora');
    } finally {
      setSavingSummaryTime(false);
    }
  };

  const handleSaveInactivityDays = async () => {
    setSavingInactivityDays(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`,
        { inactivity_days: inactivityDays },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Días de inactividad guardados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar los días de inactividad');
    } finally {
      setSavingInactivityDays(false);
    }
  };

  const handleRunInactivityCheck = async () => {
    setRunningInactivityCheck(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/settings/run-inactivity-check`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Verificación completada — ${res.data.deactivated} usuario(s) desactivado(s), ${res.data.notifications_sent} notificación(es) enviada(s).`);
      setInactivityCheckModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al ejecutar la verificación de inactividad');
    } finally {
      setRunningInactivityCheck(false);
    }
  };

  const handleSavePromoWhatsapp = async () => {
    setSavingWhatsapp(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`,
        { promo_whatsapp_number: promoWhatsapp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Número de WhatsApp guardado');
    } catch {
      toast.error('Error al guardar el número');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleSaveAiModels = async () => {
    setSavingAiModels(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`,
        {
          ai_model_chat: aiModelChat,
          ai_model_extraction: aiModelExtraction,
          ai_model_rag: aiModelRag,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Modelos de IA guardados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar los modelos de IA');
    } finally {
      setSavingAiModels(false);
    }
  };

  const handleSaveStaff = async () => {
    setSavingStaff(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`, { meeting_staff: meetingStaff }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Staff de juntas guardado');
    } catch {
      toast.error('Error al guardar el staff');
    } finally {
      setSavingStaff(false);
    }
  };

  const handleSyncDrive = async () => {
    setSyncingDrive(true);
    setSyncDriveResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/rag/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSyncDriveResult(res.data);
      toast.success(`Drive sincronizado: ${res.data.indexados} indexados, ${res.data.omitidos} omitidos`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al sincronizar Drive');
    } finally {
      setSyncingDrive(false);
    }
  };

  const handleSyncSites = async () => {
    setSyncingSites(true);
    setSyncSitesResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/rag/sync-sites`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSyncSitesResult(res.data);
      toast.success(`Sites sincronizado: ${res.data.new_indexed} nuevos, ${res.data.updated} actualizados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al sincronizar Google Sites');
    } finally {
      setSyncingSites(false);
    }
  };

  const handleForceSyncSites = async () => {
    if (!window.confirm('¿Re-indexar todas las páginas del Sites ignorando el caché? Esto puede tardar varios minutos.')) return;
    setForceSyncingSites(true);
    setSyncSitesResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/rag/sync-sites?force=true`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSyncSitesResult(res.data);
      toast.success(`Re-indexado forzado: ${res.data.new_indexed} nuevos, ${res.data.updated} actualizados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en re-indexado forzado');
    } finally {
      setForceSyncingSites(false);
    }
  };

  const handleBackfillProductos = async () => {
    setBackfillingProductos(true);
    setBackfillProductosResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/rag/backfill-productos`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setBackfillProductosResult(res.data);
      toast.success(`Productos asignados: ${res.data.updated} docs actualizados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asignar productos');
    } finally {
      setBackfillingProductos(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('La nueva contraseña y la confirmación no coinciden');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/me/change-password`,
        { old_password: oldPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contraseña actualizada exitosamente');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    }
  };

  const handleRequestEmailChange = async () => {
    if (!newEmail) {
      toast.error('Por favor ingresa el nuevo correo electrónico');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/request-email-change`,
        { new_email: newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Solicitud de cambio de correo enviada a los administradores');
      setNewEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    }
  };

  const handleSaveCedulaRc = async () => {
    if (!cedulaRcForm.cedula_vencimiento || !cedulaRcForm.rc_vencimiento) {
      toast.error('La fecha de vencimiento de cédula y RC son obligatorias');
      return;
    }
    setSavingCedulaRc(true);
    try {
      const payload = {
        cedula_vencimiento: cedulaRcForm.cedula_vencimiento,
        rc_vencimiento: cedulaRcForm.rc_vencimiento,
        cedula_inicio: cedulaRcForm.cedula_inicio || null,
        rc_inicio: cedulaRcForm.rc_inicio || null,
      };
      const { data } = await axios.put(`${API}/users/me/cedula-rc`, payload);
      setUser(data);
      toast.success('Cédula y RC actualizadas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar. Verifica las fechas.');
    } finally {
      setSavingCedulaRc(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configuración</h1>
        <p className="text-muted-foreground">Administra tu cuenta y preferencias del sistema</p>
      </div>

      {isAdmin && (
        <>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Profile */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Perfil
                </CardTitle>
                <CardDescription>Información de tu cuenta</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="bg-[#1a365d] text-white text-xl">
                      {getInitials(user?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-semibold">{user?.full_name}</h3>
                    <p className="text-muted-foreground">{user?.email}</p>
                    <Badge className={`${roleColors[user?.role]} mt-1`}>
                      {roleLabels[user?.role]}
                    </Badge>
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={user?.full_name || ''} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={user?.email || ''} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input value={user?.phone || ''} placeholder="No configurado" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Rol</Label>
                    <Input value={roleLabels[user?.role] || user?.role} disabled />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Apariencia
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                    <div>
                      <p className="font-medium">Modo Oscuro</p>
                      <p className="text-sm text-muted-foreground">
                        {theme === 'dark' ? 'Activado' : 'Desactivado'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={theme === 'dark'}
                    onCheckedChange={toggleTheme}
                    data-testid="dark-mode-toggle"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notificaciones
              </CardTitle>
              <CardDescription>Configura cómo recibir notificaciones</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notificaciones en plataforma</p>
                  <p className="text-sm text-muted-foreground">Recibe alertas dentro del CRM</p>
                </div>
                <Switch defaultChecked />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notificaciones por email</p>
                  <p className="text-sm text-muted-foreground">Próximamente disponible</p>
                </div>
                <Switch disabled />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notificaciones WhatsApp</p>
                  <p className="text-sm text-muted-foreground">
                    {promoWhatsapp ? `Activo — número: ${promoWhatsapp}` : 'Configura el número de WhatsApp de la Promotoría para activar'}
                  </p>
                </div>
                <Badge className={promoWhatsapp ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                  {promoWhatsapp ? 'Activo' : 'Sin configurar'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Seguridad - todos los usuarios */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Mi Seguridad
          </CardTitle>
          <CardDescription>Gestiona tu contraseña y correo electrónico</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Cambiar Contraseña</h3>
            <div className="grid gap-4 max-w-md">
              <div className="space-y-2">
                <Label>Contraseña Actual</Label>
                <Input
                  type="password"
                  value={oldPassword}
                  onChange={e => setOldPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label>Nueva Contraseña</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirmar Nueva Contraseña</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              >
                Cambiar Contraseña
              </Button>
            </div>
          </div>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Solicitar Cambio de Correo</h3>
            <p className="text-sm text-muted-foreground">Tu solicitud será enviada a los administradores para aprobación.</p>
            <div className="grid gap-4 max-w-md">
              <div className="space-y-2">
                <Label>Nuevo Correo Electrónico</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="nuevo@correo.com"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleRequestEmailChange}
              >
                Solicitar Cambio de Correo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mi cédula y RC - agentes/asistente/developer, cuenta activa */}
      {['agente', 'asistente', 'developer'].includes(user?.role) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5" />
              Mi Cédula y RC
            </CardTitle>
            <CardDescription>
              Mantén tu cédula profesional y póliza de responsabilidad civil vigentes. Si vencen, tu
              cuenta se suspende automáticamente — actualízalas aquí antes de que eso pase.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 max-w-xl">
              <div className="space-y-3 border rounded-lg p-4">
                <p className="text-sm font-semibold">Cédula profesional</p>
                <div className="space-y-2">
                  <Label className="text-xs">Fecha de inicio</Label>
                  <Input
                    type="date"
                    value={cedulaRcForm.cedula_inicio || ''}
                    onChange={e => setCedulaRcForm(f => ({ ...f, cedula_inicio: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Vencimiento <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={cedulaRcForm.cedula_vencimiento || ''}
                    onChange={e => setCedulaRcForm(f => ({ ...f, cedula_vencimiento: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-3 border rounded-lg p-4">
                <p className="text-sm font-semibold">Póliza RC</p>
                <div className="space-y-2">
                  <Label className="text-xs">Fecha de inicio</Label>
                  <Input
                    type="date"
                    value={cedulaRcForm.rc_inicio || ''}
                    onChange={e => setCedulaRcForm(f => ({ ...f, rc_inicio: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Vencimiento <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={cedulaRcForm.rc_vencimiento || ''}
                    onChange={e => setCedulaRcForm(f => ({ ...f, rc_vencimiento: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <Button
              onClick={handleSaveCedulaRc}
              disabled={savingCedulaRc}
              className="mt-4 bg-[#1a365d] hover:bg-[#1a365d]/90"
            >
              {savingCedulaRc ? 'Guardando...' : 'Guardar'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sistema de Alertas - visible para todos */}
      <Card className="border-2 border-[#1a365d]/20 hover:border-[#1a365d]/40 transition-colors cursor-pointer"
            onClick={() => navigate('/settings/alerts')}
            data-testid="alerts-system-link"
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-gradient-to-br from-[#1a365d] to-[#2d4a7c] text-white">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Sistema de Alertas
                  <Badge className="bg-green-100 text-green-700 text-xs">Activo</Badge>
                </CardTitle>
                <CardDescription>
                  Gestión de notificaciones programadas y cron jobs
                </CardDescription>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">
              <Zap className="h-3 w-3 mr-1" /> Cumpleaños
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Zap className="h-3 w-3 mr-1" /> Aniversarios
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Zap className="h-3 w-3 mr-1" /> Chargebacks
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Zap className="h-3 w-3 mr-1" /> Pendientes
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Zap className="h-3 w-3 mr-1" /> Renovaciones
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Admin Settings */}
      {isAdmin && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Promotoría
              </CardTitle>
              <CardDescription>Configuración de la organización</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <img src={LOGO_URL} alt="Logo" className="h-16 w-16 object-contain" />
                <div>
                  <h3 className="font-semibold">EstraPat</h3>
                  <p className="text-sm text-muted-foreground">Estrategias Patrimoniales</p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la Promotoría</Label>
                  <Input value="EstraPat" disabled />
                </div>
                <div className="space-y-2">
                  <Label>ID de Promotoría</Label>
                  <Input value="default" disabled />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-green-600" />
                WhatsApp de la Promotoría
              </CardTitle>
              <CardDescription>
                Número central desde el que se envían notificaciones automáticas a los agentes.
                Se usa como fallback cuando el agente no tiene número configurado en su perfil.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 max-w-md">
                <div className="space-y-2">
                  <Label>Número en formato E.164</Label>
                  <Input
                    value={promoWhatsapp}
                    onChange={e => setPromoWhatsapp(e.target.value)}
                    placeholder="+521XXXXXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ejemplo: +521XXXXXXXXXX (incluye el código de país). Deja vacío para desactivar.
                  </p>
                </div>
                <Button
                  onClick={handleSavePromoWhatsapp}
                  disabled={savingWhatsapp}
                  className="bg-green-600 hover:bg-green-700 text-white w-fit"
                >
                  {savingWhatsapp ? 'Guardando…' : 'Guardar número'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Modelo de IA
              </CardTitle>
              <CardDescription>
                Modelo de OpenAI usado por el bot conversacional, la extracción automática de datos
                de leads y el asistente RAG. Cambia aquí sin tocar el .env ni redesplegar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 max-w-xl">
                <div className="space-y-2">
                  <Label>Chat conversacional (Messenger / Instagram)</Label>
                  <Select value={aiModelChat} onValueChange={setAiModelChat}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      {aiModelChoices.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Genera la respuesta que ve el prospecto. Default: gpt-4.1-mini.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Extracción de datos del lead</Label>
                  <Select value={aiModelExtraction} onValueChange={setAiModelExtraction}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      {aiModelChoices.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Tarea corta de extracción JSON — usa el modelo más económico disponible. Default: gpt-4.1-nano.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Asistente RAG (documentos)</Label>
                  <Select value={aiModelRag} onValueChange={setAiModelRag}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      {aiModelChoices.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Clasificador, respuesta conversacional y respuesta documental del asistente interno. Default: gpt-4.1.
                  </p>
                </div>
                <Button
                  onClick={handleSaveAiModels}
                  disabled={savingAiModels || aiModelChoices.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white w-fit"
                >
                  {savingAiModels ? 'Guardando…' : 'Guardar modelos'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Staff de Juntas
              </CardTitle>
              <CardDescription>
                Usuarios que se agregan automáticamente al pulsar "Seleccionar Staff" al crear una junta.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <input
                type="text"
                placeholder="Buscar usuario..."
                value={staffSearch}
                onChange={e => setStaffSearch(e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded bg-background"
              />
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 bg-muted/20">
                {staffUsers
                  .filter(u => u.id !== user?.id)
                  .filter(u => (u.full_name || u.email || '').toLowerCase().includes(staffSearch.toLowerCase()))
                  .map(u => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm p-1 hover:bg-muted rounded">
                    <input
                      type="checkbox"
                      checked={meetingStaff.includes(u.id)}
                      onChange={e => setMeetingStaff(prev =>
                        e.target.checked ? [...prev, u.id] : prev.filter(id => id !== u.id)
                      )}
                      className="rounded"
                    />
                    <span>{u.full_name || u.email}</span>
                    {u.role && <span className="text-xs text-muted-foreground ml-1">({u.role})</span>}
                  </label>
                ))}
                {staffUsers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">Cargando usuarios…</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{meetingStaff.length} usuario(s) en el Staff</p>
              <Button
                onClick={handleSaveStaff}
                disabled={savingStaff}
                className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white w-fit"
              >
                {savingStaff ? 'Guardando…' : 'Guardar Staff'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-orange-500" />
                Notificaciones automáticas
              </CardTitle>
              <CardDescription>
                Controla el envío de correos y WhatsApp automáticos a los agentes
                (pagos, renovaciones, vencimientos de cédula/RC).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Estado del scheduler</p>
                  <p className="text-sm text-muted-foreground">
                    {notificationsEnabled
                      ? 'Activo — los agentes reciben correos y WA'
                      : 'Pausado — modo pruebas activo'}
                  </p>
                </div>
                <Switch
                  checked={notificationsEnabled}
                  onCheckedChange={handleToggleNotifications}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Ejecutar ahora</p>
                  <p className="text-sm text-muted-foreground">Dispara el scheduler manualmente sin esperar la hora programada</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRunSchedulerNow}
                    disabled={runningScheduler || !notificationsEnabled}
                    title={!notificationsEnabled ? 'Activa primero las notificaciones generales' : undefined}
                  >
                    {runningScheduler
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Ejecutando...</>
                      : <><Play className="h-4 w-4 mr-2" />Ejecutar ahora</>
                    }
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSyncModalOpen(true)}
                    disabled={syncingPolicies}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Sincronizar pólizas
                  </Button>
                </div>
              </div>

              <Dialog open={syncModalOpen} onOpenChange={setSyncModalOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Sincronizar estado de pólizas</DialogTitle>
                    <DialogDescription>
                      Se actualizará el estado de todas las pólizas Allianz según sus días de retraso.
                    </DialogDescription>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    ¿Deseas enviar notificaciones a los agentes al finalizar?
                  </p>
                  <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
                    <Button
                      variant="outline"
                      onClick={() => setSyncModalOpen(false)}
                      disabled={syncingPolicies}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSyncPolicies(false)}
                      disabled={syncingPolicies}
                    >
                      {syncingPolicies
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sincronizando...</>
                        : 'Solo sincronizar'
                      }
                    </Button>
                    <Button
                      onClick={() => handleSyncPolicies(true)}
                      disabled={syncingPolicies}
                    >
                      {syncingPolicies
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Procesando...</>
                        : 'Sincronizar y notificar'
                      }
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Separator />
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium">Hora de envío diario</p>
                  <p className="text-sm text-muted-foreground">El cambio aplica a partir del siguiente día</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={notificationTime}
                    onChange={e => setNotificationTime(e.target.value)}
                    className="w-32"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveNotificationTime}
                    disabled={savingNotificationTime}
                  >
                    {savingNotificationTime ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
              </div>
              <Separator />
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium">Hora de resumen diario (admins)</p>
                  <p className="text-sm text-muted-foreground">El resumen del día llega a admins después de las notificaciones principales</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={summaryTime}
                    onChange={e => setSummaryTime(e.target.value)}
                    className="w-32"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveSummaryTime}
                    disabled={savingSummaryTime}
                  >
                    {savingSummaryTime ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
              </div>
              <Separator />
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium">Días de inactividad antes de desactivar</p>
                  <p className="text-sm text-muted-foreground">Usuarios sin login en este plazo se desactivan automáticamente (todos los roles excepto admin/master). No aplica a quienes nunca han iniciado sesión.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    value={inactivityDays}
                    onChange={e => setInactivityDays(Number(e.target.value))}
                    className="w-20"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveInactivityDays}
                    disabled={savingInactivityDays}
                  >
                    {savingInactivityDays ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-sm">Verificación de inactividad</p>
                  <p className="text-sm text-muted-foreground">Corre solo esta revisión ahora mismo, sin ejecutar el resto de las notificaciones diarias. Desactiva de inmediato a quien supere el umbral, aunque el interruptor de abajo esté apagado.</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setInactivityCheckModalOpen(true)}
                  disabled={runningInactivityCheck}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Probar ahora
                </Button>
              </div>

              <Dialog open={inactivityCheckModalOpen} onOpenChange={setInactivityCheckModalOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Ejecutar verificación de inactividad</DialogTitle>
                    <DialogDescription>
                      Se evaluará a todos los usuarios (excepto admin/master) contra el umbral de {inactivityDays} día(s) configurado.
                      Quien lo supere será desactivado de inmediato y se notificará a los admins/master de su promotoría.
                      Esto corre aislado del resto del scheduler diario, y ocurre aunque el interruptor "Desactivación por inactividad" esté apagado.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
                    <Button
                      variant="outline"
                      onClick={() => setInactivityCheckModalOpen(false)}
                      disabled={runningInactivityCheck}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleRunInactivityCheck}
                      disabled={runningInactivityCheck}
                    >
                      {runningInactivityCheck
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Ejecutando...</>
                        : 'Ejecutar ahora'
                      }
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              {Object.keys(schedulers).length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-3">Tipos de notificación</p>
                    <div className="space-y-3">
                      {Object.entries(schedulers).map(([key, cfg]) => (
                        <div key={key} className="flex items-center justify-between">
                          <p className="text-sm">{cfg.label}</p>
                          <Switch
                            checked={cfg.enabled}
                            onCheckedChange={(val) => handleToggleScheduler(key, val)}
                            disabled={!notificationsEnabled}
                            title={!notificationsEnabled ? 'Activa primero las notificaciones generales' : undefined}
                          />
                        </div>
                      ))}
                    </div>
                    {!notificationsEnabled && (
                      <p className="text-xs text-muted-foreground mt-3">
                        Activa primero el scheduler global para controlar los tipos individuales.
                      </p>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5 text-purple-600" />
                Campos Personalizados de Leads
              </CardTitle>
              <CardDescription>
                Agrega campos extra a todos los leads. Los valores se pueden llenar desde la vista de edición del lead o desde el webhook. El nombre del campo (slug) se usa para matching automático en webhooks.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Campos existentes */}
              {customFields.length > 0 ? (
                <div className="space-y-2">
                  {customFields.map(cf => (
                    <div key={cf.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-sm font-medium">{cf.label}</p>
                          <p className="text-xs text-muted-foreground">
                            nombre: <code className="bg-muted px-1 rounded">{cf.name}</code>
                            {' · '}tipo: {cf.field_type}
                            {cf.field_type === 'lista' && cf.options?.length > 0 && (
                              <> · opciones: {cf.options.join(', ')}</>
                            )}
                          </p>
                        </div>
                      </div>
                      {isMaster && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteField(cf.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No hay campos personalizados. Crea el primero abajo.</p>
              )}

              <Separator />

              {/* Formulario para crear */}
              <div className="space-y-4">
                <p className="text-sm font-medium">Nuevo campo</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-1">
                    <Label>Etiqueta del campo</Label>
                    <Input
                      value={newField.label}
                      onChange={e => setNewField(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="Ej: Nivel de interés"
                    />
                    <p className="text-xs text-muted-foreground">El sistema genera automáticamente el identificador interno.</p>
                  </div>
                  <div className="space-y-1">
                    <Label>Tipo</Label>
                    <Select
                      value={newField.field_type}
                      onValueChange={v => setNewField(prev => ({ ...prev, field_type: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="texto">Texto</SelectItem>
                        <SelectItem value="numero">Número</SelectItem>
                        <SelectItem value="fecha">Fecha</SelectItem>
                        <SelectItem value="booleano">Sí / No</SelectItem>
                        <SelectItem value="lista">Lista de opciones</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newField.field_type === 'lista' && (
                    <div className="col-span-2 space-y-1">
                      <Label>Opciones (una por línea)</Label>
                      <Textarea
                        value={newField.options}
                        onChange={e => setNewField(prev => ({ ...prev, options: e.target.value }))}
                        placeholder={"Bajo\nMedio\nAlto"}
                        rows={3}
                      />
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleCreateField}
                  disabled={savingField || !newField.label}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {savingField ? 'Creando…' : 'Crear campo'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5 text-blue-600" />
                Campos Personalizados de Pólizas
              </CardTitle>
              <CardDescription>
                Agrega campos extra a todas las pólizas. Los valores se pueden llenar desde la vista de edición o desde el import. El nombre del campo (slug) se usa para matching automático en imports.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {policyCustomFields.length > 0 ? (
                <div className="space-y-2">
                  {policyCustomFields.map(cf => (
                    <div key={cf.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{cf.label}</p>
                        <p className="text-xs text-muted-foreground">
                          nombre: <code className="bg-muted px-1 rounded">{cf.name}</code>
                          {' · '}tipo: {cf.field_type}
                          {cf.field_type === 'lista' && cf.options?.length > 0 && (
                            <> · opciones: {cf.options.join(', ')}</>
                          )}
                        </p>
                      </div>
                      {isMaster && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePolicyField(cf.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No hay campos personalizados de pólizas. Crea el primero abajo.</p>
              )}

              <Separator />

              <div className="space-y-4">
                <p className="text-sm font-medium">Nuevo campo</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-1">
                    <Label>Etiqueta del campo</Label>
                    <Input
                      value={newPolicyField.label}
                      onChange={e => setNewPolicyField(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="Ej: Número de endoso"
                    />
                    <p className="text-xs text-muted-foreground">El sistema genera automáticamente el identificador interno.</p>
                  </div>
                  <div className="space-y-1">
                    <Label>Tipo</Label>
                    <Select
                      value={newPolicyField.field_type}
                      onValueChange={v => setNewPolicyField(prev => ({ ...prev, field_type: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="texto">Texto</SelectItem>
                        <SelectItem value="numero">Número</SelectItem>
                        <SelectItem value="fecha">Fecha</SelectItem>
                        <SelectItem value="booleano">Sí / No</SelectItem>
                        <SelectItem value="lista">Lista de opciones</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newPolicyField.field_type === 'lista' && (
                    <div className="col-span-2 space-y-1">
                      <Label>Opciones (una por línea)</Label>
                      <Textarea
                        value={newPolicyField.options}
                        onChange={e => setNewPolicyField(prev => ({ ...prev, options: e.target.value }))}
                        placeholder={"Bajo\nMedio\nAlto"}
                        rows={3}
                      />
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleCreatePolicyField}
                  disabled={savingPolicyField || !newPolicyField.label.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {savingPolicyField ? 'Creando…' : 'Crear campo'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {(isAdmin || isMaster) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-amber-600" />
                  Mantenimiento de Pólizas
                </CardTitle>
                <CardDescription>
                  Herramientas de reparación de datos para pólizas importadas.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">Reparar fechas de vencimiento</p>
                    <p className="text-sm text-muted-foreground">
                      Calcula end_date = fecha de inicio + 1 año para pólizas sin fecha de vencimiento (excluye productos de Ahorro).
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleRepairEndDates}
                    disabled={repairingEndDates}
                  >
                    {repairingEndDates
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reparando...</>
                      : <><Wrench className="h-4 w-4 mr-2" />Reparar fechas</>
                    }
                  </Button>
                </div>
                <Separator />
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">Corregir renovaciones sin marcar</p>
                    <p className="text-sm text-muted-foreground">
                      Marca movement_type='renovacion' en pólizas activas que son renovación de una anterior
                      pero quedaron sin marcar (reconciliaciones previas a v151). Excluye esas pólizas de sumar
                      puntos de convención como venta nueva. Seguro de correr varias veces.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleBackfillRenewals}
                    disabled={backfillingRenewals}
                  >
                    {backfillingRenewals
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Corrigiendo...</>
                      : <><RotateCcw className="h-4 w-4 mr-2" />Corregir renovaciones sin marcar</>
                    }
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Dialog open={backfillModalOpen} onOpenChange={setBackfillModalOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Resultado — Corrección de renovaciones sin marcar</DialogTitle>
                <DialogDescription>
                  Backfill v152: pólizas activas que son renovación de una anterior 'renewed' y no tenían
                  movement_type='renovacion'.
                </DialogDescription>
              </DialogHeader>
              {backfillResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="rounded-lg border p-3">
                      <p className="text-2xl font-bold">{backfillResult.total_candidatos}</p>
                      <p className="text-xs text-muted-foreground">Total candidatas</p>
                    </div>
                    <div className="rounded-lg border p-3 bg-green-50 dark:bg-green-950/30">
                      <p className="text-2xl font-bold text-green-700 dark:text-green-400">{backfillResult.actualizadas}</p>
                      <p className="text-xs text-muted-foreground">Actualizadas ahora</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-2xl font-bold">{backfillResult.ya_marcadas_previamente}</p>
                      <p className="text-xs text-muted-foreground">Ya marcadas antes</p>
                    </div>
                  </div>

                  {Object.keys(backfillResult.desglose_por_aseguradora || {}).length > 0 && (
                    <div>
                      <p className="font-medium mb-2">Desglose por aseguradora</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(backfillResult.desglose_por_aseguradora).map(([bl, n]) => (
                          <Badge key={bl} variant="secondary">{bl}: {n}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {(backfillResult.top_agentes_afectados || []).length > 0 && (
                    <div>
                      <p className="font-medium mb-2">Agentes afectados — puntos de convención</p>
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        <div className="grid grid-cols-4 gap-2 text-xs font-medium text-muted-foreground px-2">
                          <span className="col-span-2">Agente</span>
                          <span className="text-right">Antes</span>
                          <span className="text-right">Después</span>
                        </div>
                        {backfillResult.top_agentes_afectados.map((a, i) => (
                          <div key={i} className="grid grid-cols-4 gap-2 text-sm px-2 py-1.5 rounded border">
                            <span className="col-span-2 truncate">{a.agente}</span>
                            <span className="text-right">{a.puntos_antes}</span>
                            <span className="text-right">
                              {a.puntos_despues}{' '}
                              <span className={a.delta < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                                ({a.delta > 0 ? '+' : ''}{a.delta})
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setBackfillModalOpen(false)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-indigo-600" />
                Base de Conocimiento RAG
              </CardTitle>
              <CardDescription>
                Sincroniza documentos desde Google Drive y páginas del Google Sites privado para el asistente IA.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleSyncDrive}
                  disabled={syncingDrive}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {syncingDrive ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {syncingDrive ? 'Sincronizando Drive…' : 'Sync Google Drive'}
                </Button>
                <Button
                  onClick={handleSyncSites}
                  disabled={syncingSites}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {syncingSites ? (
                    <Globe className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4 mr-2" />
                  )}
                  {syncingSites ? 'Sincronizando Sites…' : 'Sync Google Sites'}
                </Button>
                <Button
                  onClick={handleForceSyncSites}
                  disabled={forceSyncingSites}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {forceSyncingSites ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {forceSyncingSites ? 'Re-indexando…' : 'Re-indexar Sites (forzar)'}
                </Button>
                <Button
                  onClick={handleBackfillProductos}
                  disabled={backfillingProductos}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {backfillingProductos ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <BookOpen className="h-4 w-4 mr-2" />
                  )}
                  {backfillingProductos ? 'Asignando…' : 'Asignar Productos'}
                </Button>
              </div>
              {syncDriveResult && (
                <div className="text-sm bg-muted rounded p-3 space-y-1">
                  <p className="font-medium">Resultado Drive:</p>
                  <p>Indexados: <span className="font-semibold text-green-700">{syncDriveResult.indexados}</span></p>
                  <p>Omitidos: <span className="font-semibold">{syncDriveResult.omitidos}</span></p>
                  {syncDriveResult.errores?.length > 0 && (
                    <p className="text-red-600">Errores: {syncDriveResult.errores.length}</p>
                  )}
                </div>
              )}
              {syncSitesResult && (
                <div className="text-sm bg-muted rounded p-3 space-y-1">
                  <p className="font-medium">Resultado Sites:</p>
                  <p>Páginas encontradas: <span className="font-semibold">{syncSitesResult.pages_found}</span></p>
                  <p>Nuevas: <span className="font-semibold text-green-700">{syncSitesResult.new_indexed}</span></p>
                  <p>Actualizadas: <span className="font-semibold text-blue-600">{syncSitesResult.updated}</span></p>
                  <p>Sin cambios: <span className="font-semibold">{syncSitesResult.skipped}</span></p>
                  {syncSitesResult.errors?.length > 0 && (
                    <p className="text-red-600">Errores: {syncSitesResult.errors.length}</p>
                  )}
                </div>
              )}
              {backfillProductosResult && (
                <div className="text-sm bg-muted rounded p-3 space-y-1">
                  <p className="font-medium">Resultado Asignar Productos:</p>
                  <p>Actualizados: <span className="font-semibold text-green-700">{backfillProductosResult.updated}</span></p>
                  <p>Sin producto detectado: <span className="font-semibold">{backfillProductosResult.skipped_null}</span></p>
                  <p>Total procesados: <span className="font-semibold">{backfillProductosResult.total}</span></p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Seguridad del Sistema
              </CardTitle>
              <CardDescription>Políticas de seguridad del sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Row Level Security (RLS)</p>
                  <p className="text-sm text-muted-foreground">
                    Los agentes solo ven sus propios datos
                  </p>
                </div>
                <Badge className="bg-green-100 text-green-800">Activo</Badge>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">RBAC</p>
                  <p className="text-sm text-muted-foreground">
                    Control de acceso basado en roles
                  </p>
                </div>
                <Badge className="bg-green-100 text-green-800">Activo</Badge>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">JWT Authentication</p>
                  <p className="text-sm text-muted-foreground">
                    Tokens seguros con expiración de 24 horas
                  </p>
                </div>
                <Badge className="bg-green-100 text-green-800">Activo</Badge>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
